import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Divider,
  Chip,
  Alert,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Checkbox,
  InputAdornment,
  CircularProgress,
  Tabs,
  Tab,
} from '@mui/material';
import {
  X,
  Search,
  Link2,
  ArrowLeftCircle,
  ArrowRightCircle,
  AlertTriangle,
  CheckCircle,
  Circle,
  Clock,
  Ban,
  PlayCircle,
  Filter,
} from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Task, TaskStatus, TaskPriority } from '@/types';
import { taskService } from '@/services/api';
import { QUERY_KEYS } from '@/services/queryClient';
import PriorityBadge from '@/shared/components/PriorityBadge';

// ============================================================================
// TYPES
// ============================================================================

interface TaskDependencyMapperProps {
  open: boolean;
  onClose: () => void;
  task: Task;
  allTasks: Task[];
  onSuccess?: () => void;
}

type DependencyDirection = 'blockedBy' | 'blocking';

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

const getStatusIcon = (status: TaskStatus) => {
  switch (status) {
    case TaskStatus.COMPLETED:
      return <CheckCircle size={14} style={{ color: '#16a34a' }} />;
    case TaskStatus.IN_PROGRESS:
      return <PlayCircle size={14} style={{ color: '#4F46E5' }} />;
    case TaskStatus.BLOCKED:
      return <Ban size={14} style={{ color: '#f59e0b' }} />;
    case TaskStatus.CANCELLED:
      return <Ban size={14} style={{ color: '#ef4444' }} />;
    default:
      return <Circle size={14} style={{ color: '#64748b' }} />;
  }
};

const getStatusLabel = (status: TaskStatus): string => {
  switch (status) {
    case TaskStatus.NOT_STARTED:
      return 'Not Started';
    case TaskStatus.IN_PROGRESS:
      return 'In Progress';
    case TaskStatus.COMPLETED:
      return 'Completed';
    case TaskStatus.BLOCKED:
      return 'Blocked';
    case TaskStatus.CANCELLED:
      return 'Cancelled';
    default:
      return status;
  }
};

/**
 * Detects if adding a dependency would create a circular reference
 */
const wouldCreateCircle = (
  fromTaskId: string,
  toTaskId: string,
  allTasks: Task[],
  direction: DependencyDirection
): boolean => {
  const visited = new Set<string>();

  const dfs = (currentId: string, targetId: string): boolean => {
    if (currentId === targetId) return true;
    if (visited.has(currentId)) return false;
    visited.add(currentId);

    const currentTask = allTasks.find((t) => t.id === currentId);
    if (!currentTask) return false;

    // Check forward dependencies (blocking)
    const dependencyIds =
      direction === 'blockedBy'
        ? currentTask.blockedByTaskIds || []
        : currentTask.blockingTaskIds || [];

    for (const depId of dependencyIds) {
      if (dfs(depId, targetId)) return true;
    }

    return false;
  };

  return dfs(toTaskId, fromTaskId);
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const TaskDependencyMapper: React.FC<TaskDependencyMapperProps> = ({
  open,
  onClose,
  task,
  allTasks,
  onSuccess,
}) => {
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [direction, setDirection] = useState<DependencyDirection>('blockedBy');
  const [selectedTaskIds, setSelectedTaskIds] = useState<string[]>([]);
  const [circularWarnings, setCircularWarnings] = useState<string[]>([]);

  // Current dependencies based on direction
  const currentDependencies = useMemo(() => {
    return direction === 'blockedBy'
      ? task.blockedByTaskIds || []
      : task.blockingTaskIds || [];
  }, [task, direction]);

  // Initialize selected tasks from current dependencies
  React.useEffect(() => {
    if (open) {
      setSelectedTaskIds(currentDependencies);
      setCircularWarnings([]);
      setSearchQuery('');
    }
  }, [open, currentDependencies]);

  // Filter available tasks (exclude self, subtasks, parent)
  const availableTasks = useMemo(() => {
    return allTasks.filter((t) => {
      // Exclude self
      if (t.id === task.id) return false;
      // Exclude subtasks
      if (t.parentTaskId === task.id) return false;
      // Exclude parent
      if (task.parentTaskId === t.id) return false;
      // Apply search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          t.title.toLowerCase().includes(query) ||
          t.description?.toLowerCase().includes(query)
        );
      }
      return true;
    });
  }, [allTasks, task, searchQuery]);

  // Mutation for updating dependencies
  const updateMutation = useMutation({
    mutationFn: async () => {
      // Calculate added and removed dependencies
      const added = selectedTaskIds.filter(
        (id) => !currentDependencies.includes(id)
      );
      const removed = currentDependencies.filter(
        (id) => !selectedTaskIds.includes(id)
      );

      // Build update payload
      const updatePayload: Partial<Task> =
        direction === 'blockedBy'
          ? { blockedByTaskIds: selectedTaskIds }
          : { blockingTaskIds: selectedTaskIds };

      // Update the main task
      await taskService.updateTask(task.id, updatePayload);

      // Update the inverse relationship on affected tasks
      for (const addedId of added) {
        const affectedTask = allTasks.find((t) => t.id === addedId);
        if (affectedTask) {
          const inverseKey =
            direction === 'blockedBy' ? 'blockingTaskIds' : 'blockedByTaskIds';
          const inverseIds = affectedTask[inverseKey] || [];
          if (!inverseIds.includes(task.id)) {
            await taskService.updateTask(addedId, {
              [inverseKey]: [...inverseIds, task.id],
            });
          }
        }
      }

      for (const removedId of removed) {
        const affectedTask = allTasks.find((t) => t.id === removedId);
        if (affectedTask) {
          const inverseKey =
            direction === 'blockedBy' ? 'blockingTaskIds' : 'blockedByTaskIds';
          const inverseIds = affectedTask[inverseKey] || [];
          await taskService.updateTask(removedId, {
            [inverseKey]: inverseIds.filter((id) => id !== task.id),
          });
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.tasks });
      if (task.projectId) {
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.projectTasks(task.projectId),
        });
      }
      onSuccess?.();
      handleClose();
    },
  });

  const handleClose = () => {
    setSearchQuery('');
    setSelectedTaskIds([]);
    setCircularWarnings([]);
    onClose();
  };

  const handleToggleTask = (taskId: string) => {
    const isSelected = selectedTaskIds.includes(taskId);

    if (!isSelected) {
      // Check for circular dependency
      const wouldCircle = wouldCreateCircle(task.id, taskId, allTasks, direction);
      if (wouldCircle) {
        setCircularWarnings((prev) => [...prev, taskId]);
        return;
      }
    }

    // Clear warning if deselecting
    setCircularWarnings((prev) => prev.filter((id) => id !== taskId));

    setSelectedTaskIds((prev) =>
      isSelected ? prev.filter((id) => id !== taskId) : [...prev, taskId]
    );
  };

  const handleSave = () => {
    if (circularWarnings.length > 0) return;
    updateMutation.mutate();
  };

  const hasChanges = useMemo(() => {
    const sortedCurrent = [...currentDependencies].sort();
    const sortedSelected = [...selectedTaskIds].sort();
    return JSON.stringify(sortedCurrent) !== JSON.stringify(sortedSelected);
  }, [currentDependencies, selectedTaskIds]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2, maxHeight: '80vh' },
      }}
    >
      <DialogTitle>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Link2 size={20} style={{ color: '#64748b' }} />
            <Typography variant="h6" fontWeight={600}>
              Manage Dependencies
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <X size={20} />
          </IconButton>
        </Box>
      </DialogTitle>

      <Divider />

      {/* Current Task Info */}
      <Box
        sx={{
          px: 3,
          py: 1.5,
          bgcolor: 'grey.50',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="caption" color="text.secondary" display="block">
          Task
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
          {getStatusIcon(task.status)}
          <Typography variant="body2" fontWeight={500} noWrap>
            {task.title}
          </Typography>
        </Box>
      </Box>

      {/* Direction Tabs */}
      <Box sx={{ px: 3, pt: 2 }}>
        <Tabs
          value={direction}
          onChange={(_, newValue) => {
            setDirection(newValue);
            setSelectedTaskIds(
              newValue === 'blockedBy'
                ? task.blockedByTaskIds || []
                : task.blockingTaskIds || []
            );
            setCircularWarnings([]);
          }}
          sx={{
            minHeight: 36,
            '& .MuiTab-root': {
              minHeight: 36,
              textTransform: 'none',
            },
          }}
        >
          <Tab
            value="blockedBy"
            icon={<ArrowLeftCircle size={16} />}
            iconPosition="start"
            label="Blocked By"
          />
          <Tab
            value="blocking"
            icon={<ArrowRightCircle size={16} />}
            iconPosition="start"
            label="Blocking"
          />
        </Tabs>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {direction === 'blockedBy'
            ? 'Select tasks that must be completed before this task can start.'
            : 'Select tasks that are waiting for this task to be completed.'}
        </Typography>
      </Box>

      <DialogContent sx={{ pt: 2 }}>
        {/* Search */}
        <TextField
          fullWidth
          size="small"
          placeholder="Search tasks..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={18} style={{ color: '#9ca3af' }} />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />

        {/* Circular Warning */}
        {circularWarnings.length > 0 && (
          <Alert
            severity="warning"
            icon={<AlertTriangle size={18} />}
            sx={{ mb: 2 }}
          >
            <Typography variant="body2">
              Cannot add this dependency - it would create a circular reference.
            </Typography>
          </Alert>
        )}

        {/* Task List */}
        <Box
          sx={{
            maxHeight: 300,
            overflow: 'auto',
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
          }}
        >
          {availableTasks.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {searchQuery
                  ? 'No tasks match your search'
                  : 'No available tasks for dependency'}
              </Typography>
            </Box>
          ) : (
            <List disablePadding>
              {availableTasks.map((availableTask, index) => {
                const isSelected = selectedTaskIds.includes(availableTask.id);
                const hasCircularWarning = circularWarnings.includes(
                  availableTask.id
                );

                return (
                  <React.Fragment key={availableTask.id}>
                    {index > 0 && <Divider />}
                    <ListItem disablePadding>
                      <ListItemButton
                        onClick={() => handleToggleTask(availableTask.id)}
                        sx={{
                          py: 1,
                          bgcolor: hasCircularWarning
                            ? 'error.50'
                            : isSelected
                            ? 'primary.50'
                            : 'transparent',
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Checkbox
                            checked={isSelected}
                            disabled={hasCircularWarning}
                            size="small"
                            sx={{
                              color: hasCircularWarning
                                ? 'error.main'
                                : undefined,
                            }}
                          />
                        </ListItemIcon>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          {getStatusIcon(availableTask.status)}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  textDecoration:
                                    availableTask.status === TaskStatus.COMPLETED
                                      ? 'line-through'
                                      : 'none',
                                }}
                              >
                                {availableTask.title}
                              </Typography>
                              {hasCircularWarning && (
                                <Chip
                                  size="small"
                                  icon={<AlertTriangle size={10} />}
                                  label="Circular"
                                  sx={{
                                    height: 18,
                                    fontSize: '0.6rem',
                                    bgcolor: 'error.100',
                                    color: 'error.dark',
                                    '& .MuiChip-icon': { color: 'inherit' },
                                  }}
                                />
                              )}
                            </Box>
                          }
                          secondary={
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {getStatusLabel(availableTask.status)}
                            </Typography>
                          }
                        />
                        <PriorityBadge
                          priority={availableTask.priority}
                          size="small"
                        />
                      </ListItemButton>
                    </ListItem>
                  </React.Fragment>
                );
              })}
            </List>
          )}
        </Box>

        {/* Selection Summary */}
        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Selected: {selectedTaskIds.length} task
            {selectedTaskIds.length !== 1 ? 's' : ''}
          </Typography>
          {hasChanges && (
            <Chip
              size="small"
              label="Changed"
              sx={{
                height: 18,
                fontSize: '0.6rem',
                bgcolor: 'info.100',
                color: 'info.dark',
              }}
            />
          )}
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} variant="outlined" color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={
            !hasChanges ||
            circularWarnings.length > 0 ||
            updateMutation.isPending
          }
          startIcon={
            updateMutation.isPending ? (
              <CircularProgress size={16} />
            ) : (
              <Link2 size={16} />
            )
          }
        >
          {updateMutation.isPending ? 'Saving...' : 'Save Dependencies'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TaskDependencyMapper;
